import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Camera,
    Eye,
    EyeOff,
    Loader2,
    Lock,
    LogOut,
    ShieldCheck,
    Trash2,
    User,
    UserCircle2,
} from "lucide-react";
import { toast } from "sonner";
import {
    changeProfilimSifre,
    removeProfilimAvatar,
    updateProfilim,
    uploadProfilimAvatar,
    absoluteUrl,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

function initialsFromUser(user) {
    const name = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
    if (name) {
        const parts = name.split(/\s+/);
        return parts
            .slice(0, 2)
            .map((p) => p[0])
            .join("")
            .toUpperCase();
    }
    return (user?.username || "MB").slice(0, 2).toUpperCase();
}

function FieldError({ message }) {
    if (!message) return null;
    return <p className="text-xs text-red-400 mt-1">{message}</p>;
}

export default function ProfileSettingsDialog({ open, onOpenChange }) {
    const { user, refreshProfile } = useAuth();
    const fileRef = useRef(null);

    const [tab, setTab] = useState("profil");
    const [profileForm, setProfileForm] = useState({
        first_name: "",
        last_name: "",
        email: "",
    });
    const [profileErrors, setProfileErrors] = useState({});
    const [profileSaving, setProfileSaving] = useState(false);

    const [passwordForm, setPasswordForm] = useState({
        mevcut_sifre: "",
        yeni_sifre: "",
        yeni_sifre_tekrar: "",
    });
    const [passwordErrors, setPasswordErrors] = useState({});
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const [avatarUploading, setAvatarUploading] = useState(false);
    const [avatarRemoving, setAvatarRemoving] = useState(false);
    const [avatarPreview, setAvatarPreview] = useState(null);

    useEffect(() => {
        if (!open || !user) return;
        setProfileForm({
            first_name: user.first_name || "",
            last_name: user.last_name || "",
            email: user.email || "",
        });
        setProfileErrors({});
        setPasswordForm({
            mevcut_sifre: "",
            yeni_sifre: "",
            yeni_sifre_tekrar: "",
        });
        setPasswordErrors({});
        setAvatarPreview(null);
        setTab("profil");
    }, [open, user]);

    const displayName = useMemo(() => {
        const full = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
        return full || user?.full_name || user?.username || "Yönetici";
    }, [user]);

    const handleProfileSave = async (e) => {
        e.preventDefault();
        setProfileSaving(true);
        setProfileErrors({});
        try {
            const data = await updateProfilim(profileForm);
            await refreshProfile(data);
            toast.success("Profil bilgileriniz güncellendi.");
        } catch (err) {
            const errors = err?.response?.data?.errors || {};
            setProfileErrors(errors);
            toast.error(err?.response?.data?.detail || "Profil güncellenemedi.");
        } finally {
            setProfileSaving(false);
        }
    };

    const handlePasswordSave = async (e) => {
        e.preventDefault();
        setPasswordSaving(true);
        setPasswordErrors({});

        const localErrors = {};
        if (!passwordForm.mevcut_sifre) {
            localErrors.mevcut_sifre = "Mevcut şifrenizi girin.";
        }
        if (!passwordForm.yeni_sifre) {
            localErrors.yeni_sifre = "Yeni şifre gerekli.";
        } else if (passwordForm.yeni_sifre.length < 8) {
            localErrors.yeni_sifre = "En az 8 karakter olmalıdır.";
        }
        if (!passwordForm.yeni_sifre_tekrar) {
            localErrors.yeni_sifre_tekrar = "Yeni şifreyi tekrar girin.";
        } else if (
            passwordForm.yeni_sifre &&
            passwordForm.yeni_sifre !== passwordForm.yeni_sifre_tekrar
        ) {
            localErrors.yeni_sifre_tekrar = "Şifreler eşleşmiyor.";
        }

        if (Object.keys(localErrors).length) {
            setPasswordErrors(localErrors);
            setPasswordSaving(false);
            return;
        }

        try {
            const res = await changeProfilimSifre(passwordForm);
            setPasswordForm({
                mevcut_sifre: "",
                yeni_sifre: "",
                yeni_sifre_tekrar: "",
            });
            toast.success(res?.detail || "Şifreniz güncellendi.");
        } catch (err) {
            const errors = err?.response?.data?.errors || {};
            setPasswordErrors(errors);
            toast.error(err?.response?.data?.detail || "Şifre değiştirilemedi.");
        } finally {
            setPasswordSaving(false);
        }
    };

    const validateAvatarFile = (file) => {
        if (!file) return "Dosya seçin.";
        const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
        if (!allowed.includes(file.type)) {
            return "Yalnızca JPEG, PNG, WebP veya GIF yükleyebilirsiniz.";
        }
        if (file.size > 5 * 1024 * 1024) {
            return "Dosya boyutu en fazla 5 MB olabilir.";
        }
        return null;
    };

    const handleAvatarSelect = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        const validationError = validateAvatarFile(file);
        if (validationError) {
            toast.error(validationError);
            return;
        }

        setAvatarPreview(URL.createObjectURL(file));
        setAvatarUploading(true);
        try {
            const data = await uploadProfilimAvatar(file);
            await refreshProfile(data);
            toast.success("Profil fotoğrafınız güncellendi.");
        } catch (err) {
            setAvatarPreview(null);
            const msg =
                err?.response?.data?.errors?.avatar ||
                err?.response?.data?.detail ||
                "Fotoğraf yüklenemedi.";
            toast.error(msg);
        } finally {
            setAvatarUploading(false);
        }
    };

    const handleAvatarRemove = async () => {
        setAvatarRemoving(true);
        try {
            const data = await removeProfilimAvatar();
            setAvatarPreview(null);
            await refreshProfile(data);
            toast.success("Profil fotoğrafı kaldırıldı.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Fotoğraf kaldırılamadı.");
        } finally {
            setAvatarRemoving(false);
        }
    };

    useEffect(() => {
        return () => {
            if (avatarPreview) URL.revokeObjectURL(avatarPreview);
        };
    }, [avatarPreview]);

    const avatarSrc = avatarPreview || absoluteUrl(user?.avatar_url) || null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="profile-settings-dialog"
                className="max-w-xl border-white/10 bg-[#121A2F] text-slate-100 p-0 overflow-hidden"
            >
                <div className="border-b border-white/10 bg-gradient-to-r from-[#8b1f2f]/20 via-[#152a45]/40 to-transparent px-6 py-5">
                    <DialogHeader className="text-left space-y-3">
                        <div className="flex items-center gap-4">
                            <Avatar className="h-16 w-16 border-2 border-[rgba(201,169,97,0.35)] shadow-lg">
                                {avatarSrc ? (
                                    <AvatarImage src={avatarSrc} alt={displayName} />
                                ) : null}
                                <AvatarFallback className="bg-gradient-to-br from-[#8b1f2f] to-[#152a45] text-[var(--mbs-gold-soft)] text-lg font-semibold">
                                    {initialsFromUser(user)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                                <DialogTitle className="text-xl font-display gold-text">
                                    Hesap Ayarları
                                </DialogTitle>
                                <DialogDescription className="text-slate-400">
                                    {displayName} · @{user?.username}
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>
                </div>

                <Tabs value={tab} onValueChange={setTab} className="px-6 pb-6">
                    <TabsList className="grid w-full grid-cols-3 bg-[#0B0F19]/80 border border-white/10">
                        <TabsTrigger value="profil" className="gap-1.5">
                            <User className="h-3.5 w-3.5" />
                            Profil
                        </TabsTrigger>
                        <TabsTrigger value="sifre" className="gap-1.5">
                            <Lock className="h-3.5 w-3.5" />
                            Şifre
                        </TabsTrigger>
                        <TabsTrigger value="foto" className="gap-1.5">
                            <Camera className="h-3.5 w-3.5" />
                            Fotoğraf
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="profil" className="mt-5 space-y-4">
                        <form onSubmit={handleProfileSave} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">
                                        Ad
                                    </label>
                                    <Input
                                        value={profileForm.first_name}
                                        onChange={(e) =>
                                            setProfileForm((f) => ({
                                                ...f,
                                                first_name: e.target.value,
                                            }))
                                        }
                                        className="bg-[#0B0F19] border-white/10"
                                        placeholder="Adınız"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">
                                        Soyad
                                    </label>
                                    <Input
                                        value={profileForm.last_name}
                                        onChange={(e) =>
                                            setProfileForm((f) => ({
                                                ...f,
                                                last_name: e.target.value,
                                            }))
                                        }
                                        className="bg-[#0B0F19] border-white/10"
                                        placeholder="Soyadınız"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">
                                    E-posta
                                </label>
                                <Input
                                    type="email"
                                    value={profileForm.email}
                                    onChange={(e) =>
                                        setProfileForm((f) => ({ ...f, email: e.target.value }))
                                    }
                                    className="bg-[#0B0F19] border-white/10"
                                    placeholder="ornek@mbshukuk.com"
                                />
                                <FieldError message={profileErrors.email} />
                            </div>
                            <div className="rounded-lg border border-white/10 bg-[#0B0F19]/60 px-3 py-2 text-xs text-slate-500 flex items-center gap-2">
                                <ShieldCheck className="h-3.5 w-3.5 text-[var(--mbs-gold-soft)]" />
                                Kullanıcı adı: <span className="text-slate-300">{user?.username}</span>
                            </div>
                            <div className="flex justify-end pt-2">
                                <Button
                                    type="submit"
                                    disabled={profileSaving}
                                    className="bg-[#8b1f2f] hover:bg-[#a02436] text-white min-w-[120px]"
                                >
                                    {profileSaving ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        "Kaydet"
                                    )}
                                </Button>
                            </div>
                        </form>
                    </TabsContent>

                    <TabsContent value="sifre" className="mt-5 space-y-4">
                        <form onSubmit={handlePasswordSave} className="space-y-4">
                            <PasswordField
                                label="Mevcut Şifre"
                                value={passwordForm.mevcut_sifre}
                                onChange={(v) =>
                                    setPasswordForm((f) => ({ ...f, mevcut_sifre: v }))
                                }
                                show={showCurrent}
                                onToggle={() => setShowCurrent((s) => !s)}
                                error={passwordErrors.mevcut_sifre}
                                autoComplete="current-password"
                            />
                            <PasswordField
                                label="Yeni Şifre"
                                value={passwordForm.yeni_sifre}
                                onChange={(v) =>
                                    setPasswordForm((f) => ({ ...f, yeni_sifre: v }))
                                }
                                show={showNew}
                                onToggle={() => setShowNew((s) => !s)}
                                error={passwordErrors.yeni_sifre}
                                autoComplete="new-password"
                                hint="En az 8 karakter, mevcut şifreden farklı olmalı."
                            />
                            <PasswordField
                                label="Yeni Şifre (Tekrar)"
                                value={passwordForm.yeni_sifre_tekrar}
                                onChange={(v) =>
                                    setPasswordForm((f) => ({ ...f, yeni_sifre_tekrar: v }))
                                }
                                show={showConfirm}
                                onToggle={() => setShowConfirm((s) => !s)}
                                error={passwordErrors.yeni_sifre_tekrar}
                                autoComplete="new-password"
                            />
                            <div className="flex justify-end pt-2">
                                <Button
                                    type="submit"
                                    disabled={passwordSaving}
                                    className="bg-[#8b1f2f] hover:bg-[#a02436] text-white min-w-[140px]"
                                >
                                    {passwordSaving ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        "Şifreyi Güncelle"
                                    )}
                                </Button>
                            </div>
                        </form>
                    </TabsContent>

                    <TabsContent value="foto" className="mt-5">
                        <div className="rounded-xl border border-white/10 bg-[#0B0F19]/60 p-6 flex flex-col items-center text-center gap-4">
                            <Avatar className="h-28 w-28 border-2 border-[rgba(201,169,97,0.35)]">
                                {avatarSrc ? (
                                    <AvatarImage src={avatarSrc} alt={displayName} />
                                ) : null}
                                <AvatarFallback className="bg-gradient-to-br from-[#8b1f2f] to-[#152a45] text-[var(--mbs-gold-soft)] text-2xl font-semibold">
                                    {initialsFromUser(user)}
                                </AvatarFallback>
                            </Avatar>
                            <p className="text-sm text-slate-400 max-w-sm">
                                JPEG, PNG, WebP veya GIF — en fazla 5 MB. Kare fotoğraflar en iyi
                                görünür.
                            </p>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                className="hidden"
                                onChange={handleAvatarSelect}
                            />
                            <div className="flex flex-wrap items-center justify-center gap-2">
                                <Button
                                    type="button"
                                    onClick={() => fileRef.current?.click()}
                                    disabled={avatarUploading}
                                    className="bg-[#8b1f2f] hover:bg-[#a02436] text-white"
                                >
                                    {avatarUploading ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    ) : (
                                        <Camera className="h-4 w-4 mr-2" />
                                    )}
                                    Fotoğraf Yükle
                                </Button>
                                {(user?.avatar_url || avatarPreview) && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleAvatarRemove}
                                        disabled={avatarRemoving || avatarUploading}
                                        className="border-white/15 text-slate-300 hover:bg-white/5"
                                    >
                                        {avatarRemoving ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        ) : (
                                            <Trash2 className="h-4 w-4 mr-2" />
                                        )}
                                        Kaldır
                                    </Button>
                                )}
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}

function PasswordField({
    label,
    value,
    onChange,
    show,
    onToggle,
    error,
    autoComplete,
    hint,
}) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">
                {label}
            </label>
            <div className="relative">
                <Input
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    autoComplete={autoComplete}
                    className="bg-[#0B0F19] border-white/10 pr-10"
                />
                <button
                    type="button"
                    onClick={onToggle}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 p-1"
                    tabIndex={-1}
                >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
            </div>
            {hint && !error ? <p className="text-[11px] text-slate-500 mt-1">{hint}</p> : null}
            <FieldError message={error} />
        </div>
    );
}

export function UserAvatarBadge({ user, size = "md", className = "" }) {
    const sizes = {
        sm: "h-8 w-8 text-[10px]",
        md: "h-9 w-9 text-xs",
        lg: "h-10 w-10 text-sm",
    };
    return (
        <Avatar className={`${sizes[size] || sizes.md} border border-[rgba(201,169,97,0.25)] ${className}`}>
            {user?.avatar_url ? (
                <AvatarImage src={absoluteUrl(user.avatar_url)} alt={user.username} />
            ) : null}
            <AvatarFallback className="bg-gradient-to-br from-[#8b1f2f] to-[#152a45] text-[var(--mbs-gold-soft)] font-semibold">
                {initialsFromUser(user)}
            </AvatarFallback>
        </Avatar>
    );
}

export function UserProfileMenu({ variant = "header", showNotifications = false }) {
    const { user, signOut } = useAuth();
    const [settingsOpen, setSettingsOpen] = useState(false);

    const displayName = useMemo(() => {
        const full = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
        return full || user?.full_name || user?.username || "Yönetici";
    }, [user]);

    const openSettings = useCallback(() => setSettingsOpen(true), []);

    if (variant === "sidebar") {
        return (
            <>
                <button
                    type="button"
                    data-testid="sidebar-profile-button"
                    onClick={openSettings}
                    className="flex items-center gap-3 min-w-0 flex-1 text-left rounded-lg px-1 py-1 hover:bg-[rgba(139,31,47,0.12)] transition group"
                >
                    <UserAvatarBadge user={user} />
                    <div className="min-w-0">
                        <div className="text-sm text-white truncate group-hover:text-[var(--mbs-gold-soft)]">
                            {displayName}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                            Profil · Ayarlar
                        </div>
                    </div>
                </button>
                <ProfileSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
            </>
        );
    }

    return (
        <>
            <div className="flex items-center gap-2">
                {showNotifications ? null : null}
                <button
                    type="button"
                    data-testid="header-profile-button"
                    onClick={openSettings}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#121A2F]/80 px-2.5 py-1.5 hover:border-[rgba(201,169,97,0.35)] hover:bg-[#152a45]/50 transition"
                >
                    <UserAvatarBadge user={user} size="sm" />
                    <div className="hidden sm:block text-left min-w-0 max-w-[140px]">
                        <div className="text-sm text-white truncate">{displayName}</div>
                        <div className="text-[10px] text-slate-500 truncate">@{user?.username}</div>
                    </div>
                    <UserCircle2 className="h-4 w-4 text-slate-500 hidden sm:block" />
                </button>
                <button
                    data-testid="header-logout-button"
                    onClick={signOut}
                    title="Çıkış Yap"
                    className="h-9 w-9 rounded-lg border border-white/10 hover:bg-[rgba(139,31,47,0.15)] flex items-center justify-center text-slate-400 hover:text-[var(--mbs-gold-soft)] transition"
                >
                    <LogOut className="h-4 w-4" />
                </button>
            </div>
            <ProfileSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
        </>
    );
}
